import { createI18n } from 'vue-i18n';
import { plgPlanSDK } from '../../index';
import { getCurrentLanguage } from '@eureka/language-tool';
import type { PlgPlanSDKOpenOptions, PlgPlanSDKOptions } from '../types';
import { tryOnBeforeMount } from '@vueuse/core';
import { removeAuthToken } from '../utils';
import _ from 'lodash';

// 从 localStorage 获取 token
function getTokenFromStorage(): string {
    try {
        const possibleKeys = ['patsnap-authorizations', 'access_token', 'authToken', 'token']
        for (const key of possibleKeys) {
            const storedData = localStorage.getItem(key)
            if (!storedData) continue

            // 纯 token 字符串
            if (storedData.startsWith('eyJ')) {
                return storedData
            }

            // 尝试解析 JSON
            try {
                const tokenData = JSON.parse(storedData)
                const token = tokenData.token || tokenData.access_token || tokenData.refreshToken || ''
                if (token) return token
            } catch {
                // 尝试正则提取
                const tokenMatch = storedData.match(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/)
                if (tokenMatch) return tokenMatch[0]
            }
        }
        return ''
    } catch {
        return ''
    }
}

// 获取认证 headers
function getAuthHeaders(): Record<string, string> {
    const token = getTokenFromStorage()
    if (!token) return {}

    const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
    }

    // 解析 JWT 获取用户信息
    try {
        const strings = token.split('.')
        if (strings.length >= 2) {
            const jwt = JSON.parse(decodeURIComponent(escape(window.atob(strings[1].replace(/-/g, '+').replace(/_/g, '/')))))
            if (jwt.sub) headers['X-User-ID'] = jwt.sub
            if (jwt.tenant_id) headers['X-Tenant-ID'] = jwt.tenant_id
            if (jwt.client_id) headers['X-Client-ID'] = jwt.client_id
        }
    } catch {}

    return headers
}

// 获取 Vault API 基础地址
function getVaultApiBaseUrl(): string {
    // 开发环境：返回空字符串，让 Vite 代理生效
    if (import.meta.env?.DEV) {
        return import.meta.env?.VITE_API_BASE_URL || ''
    }

    // 生产环境：使用 window.process.env.VUE_APP_VAULT_API_URI
    const windowProcess = (window as any).process
    const envValue = windowProcess?.env?.VUE_APP_VAULT_API_URI

    // 检查环境变量是否有效
    const isValidEnvValue = envValue &&
        envValue !== '' &&
        envValue !== 'https://qa-eureka-service.zhihuiya.com/vault' &&
        !envValue.includes('{{') &&
        !envValue.includes('${')

    if (isValidEnvValue) {
        return envValue
    }

    // Fallback: 根据当前域名自动推断 Vault 后端地址
    const hostname = window.location.hostname
    const match = hostname.match(/^(qa-|stage-)?eureka\.zhihuiya\.com$/)
    if (match) {
        const prefix = match[1] || ''
        return `https://${prefix}eureka-service.zhihuiya.com/vault`
    }

    // 默认使用 stage 环境
    return 'https://stage-eureka-service.zhihuiya.com/vault'
}

export function usePlgPlanSdk(options?: PlgPlanSDKOptions & { router?: any; route?: any }) {
    plgPlanSDK.setI18n(options?.i18n || createI18n({
        locale: getCurrentLanguage(),
        legacy: false,
    }))

    // 处理支付成功回调
    if (options?.router && options?.route) {
        tryOnBeforeMount(async () => {
            const { payment_state } = options.route.query;
            if (payment_state === 'success') {
                removeAuthToken()
                plgPlanSDK.openSuccessDialog()
                options.router.replace({
                    query: {
                        ..._.omit(options.route.query, ['payment_state']),
                    },
                })

                // 付款成功后刷新用量缓存
                try {
                    const baseUrl = getVaultApiBaseUrl()
                    await fetch(`${baseUrl}/api/v1/files/capacity/refresh-cache`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...getAuthHeaders(),
                        },
                    })
                    console.log('[Payment] 用量缓存刷新成功')
                } catch (error) {
                    console.error('[Payment] 用量缓存刷新失败:', error)
                }
            }
        })
    }

    function openPlgPlanDialog(options?: PlgPlanSDKOpenOptions) {
        plgPlanSDK.openPlgPlanDialog(options)
    }

    function openSuccessDialog() {
        plgPlanSDK.openSuccessDialog()
    }

    return {
        openPlgPlanDialog,
        openSuccessDialog,
    }
}
